import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Megaphone, Users, Send, Beaker, Sparkles, RefreshCw, CheckCircle2,
  AlertTriangle, Loader2,
} from "lucide-react";

const TIERS = ["free", "workshop", "course", "membership", "cohort"];

export default function BroadcastAdminTab() {
  const [audience, setAudience] = useState("all_members");
  const [tier, setTier] = useState("membership");
  const [customEmails, setCustomEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [count, setCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/broadcasts");
      setHistory(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const audiencePayload = useMemo(() => {
    const p = { audience };
    if (audience === "by_tier") p.tier = tier;
    if (audience === "custom") p.custom_emails = customEmails
      .split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    return p;
  }, [audience, tier, customEmails]);

  const previewCount = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/admin/broadcast/preview", audiencePayload);
      setCount(data.count);
      toast.success(`${data.count} recipients`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const doSend = async (testOnly = false) => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error("Subject and body are required."); return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", {
        ...audiencePayload,
        subject: subject.trim(),
        body_html: bodyHtml,
        test_only: testOnly,
      });
      toast.success(testOnly ? "Test email sent to you" : `Queued ${data.queued} recipients`);
      if (!testOnly) { setSubject(""); setBodyHtml(""); setCount(null); }
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="broadcast-tab">
      <div>
        <div className="flex items-center gap-2 text-white/85">
          <Megaphone size={18} className="text-[#FF8A00]"/>
          <span className="font-display text-xl">Broadcast</span>
        </div>
        <p className="text-sm text-white/50 mt-1">
          Send a marketing email to your members or newsletter list. Every message includes a one-click unsubscribe link — only opted-in recipients are targeted.
        </p>
      </div>

      <div className="glass rounded-2xl p-6 space-y-5">
        {/* Audience */}
        <div>
          <Label className="text-xs text-white/60">Audience</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {[
              ["all_members", "All active members"],
              ["by_tier", "By tier"],
              ["newsletter", "Newsletter subscribers"],
              ["custom", "Custom emails"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => { setAudience(v); setCount(null); }}
                data-testid={`broadcast-aud-${v}`}
                className={`px-3 py-2 rounded-lg text-sm text-left border ${
                  audience === v
                    ? "bg-[#FF8A00]/15 border-[#FF8A00]/40 text-white"
                    : "bg-[#0F0F0F] border-white/10 text-white/70 hover:bg-white/[0.04]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {audience === "by_tier" && (
          <div>
            <Label className="text-xs text-white/60">Tier</Label>
            <Select value={tier} onValueChange={(v) => { setTier(v); setCount(null); }}>
              <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1"
                data-testid="broadcast-tier">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {audience === "custom" && (
          <div>
            <Label className="text-xs text-white/60">Custom recipients (one per line or comma-separated)</Label>
            <Textarea rows={4} value={customEmails}
              onChange={(e) => { setCustomEmails(e.target.value); setCount(null); }}
              data-testid="broadcast-custom-emails"
              placeholder="jane@example.com&#10;john@example.com"
              className="bg-[#0F0F0F] border-white/10 mt-1 font-mono text-xs"/>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3 py-2 border-y border-white/5">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Users size={14}/>
            {count === null ? "Preview to see recipient count" : (
              <span>Recipients matched: <span className="text-white font-semibold">{count}</span></span>
            )}
          </div>
          <Button onClick={previewCount} disabled={previewLoading}
            variant="outline" data-testid="broadcast-preview"
            className="rounded-full bg-transparent border-white/15 hover:bg-white/5">
            {previewLoading ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <RefreshCw size={13} className="mr-1.5"/>}
            Preview count
          </Button>
        </div>

        {/* Message */}
        <div>
          <Label className="text-xs text-white/60">Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="A new protocol drop just landed"
            data-testid="broadcast-subject"
            className="bg-[#0F0F0F] border-white/10 mt-1"/>
        </div>
        <div>
          <Label className="text-xs text-white/60">Body (HTML supported — links, bold, lists)</Label>
          <Textarea rows={10} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
            placeholder="<p>Hey team,</p><p>Dr. Slava just released the new autumn peptide protocol...</p>"
            data-testid="broadcast-body"
            className="bg-[#0F0F0F] border-white/10 mt-1 font-mono text-xs"/>
          <p className="text-[11px] text-white/40 mt-1">The email is auto-wrapped in the Dr Thrive 365 template with an unsubscribe footer.</p>
        </div>

        <div className="flex gap-2 justify-end flex-wrap">
          <Button onClick={() => doSend(true)} disabled={sending}
            variant="outline" data-testid="broadcast-test"
            className="rounded-full bg-transparent border-white/15 hover:bg-white/5">
            <Beaker size={13} className="mr-1.5"/> {sending ? "Sending..." : "Test send to me"}
          </Button>
          <Button onClick={() => doSend(false)}
            disabled={sending || !subject.trim() || !bodyHtml.trim()}
            data-testid="broadcast-send"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] disabled:opacity-40">
            <Send size={13} className="mr-1.5"/> {sending ? "Queueing..." : "Send broadcast"}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={14} className="text-[#FF8A00]"/>
          <div className="font-display text-lg">Recent broadcasts</div>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-white/40">Nothing sent yet — your first broadcast will appear here.</div>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.broadcast_id}
                data-testid={`broadcast-row-${b.broadcast_id}`}
                className="rounded-xl border border-white/5 bg-[#0F0F0F] p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{b.subject}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {b.audience === "by_tier" ? `Tier: ${b.tier}` : b.audience.replace("_"," ")}
                    {" · "}
                    {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className={b.status === "completed" ? "text-emerald-300" : "text-[#FF8A00]"}>
                    {b.status}
                  </div>
                  <div className="text-white/50">{b.sent || 0} / {b.recipient_count} sent</div>
                  {b.failed > 0 && <div className="text-[#E52E71]">{b.failed} failed</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
